import {
  TaskStatus,
  ChallengeTask,
  ChallengeCampaignEventState,
} from "@/types";
import { getMaxPoints } from "@/lib/challenges";
import { cn } from "@/helpers/cn";
import { ArrowRight, CheckCircle2, Clock } from "lucide-react";
import { QuestLogo } from "@/components/quest-logo";
import { LockOverlay } from "./lock-overlay";

interface QuestCardProps {
  quest: TaskStatus;
  challengeTask: ChallengeTask | undefined;
  onClick: () => void;
  disabled?: boolean;
  isLocked?: boolean;
  unlockMessage?: string;
}

export const QuestCard = ({
  quest,
  challengeTask,
  onClick,
  disabled = false,
  isLocked = false,
  unlockMessage = "Locked",
}: QuestCardProps) => {
  const maxPoints = challengeTask ? getMaxPoints(challengeTask) : parseFloat(quest.maxPoints);
  const shouldBeDisabled = disabled || isLocked;

  // Format points with no decimals
  const formattedPoints = Math.floor(maxPoints).toLocaleString();

  return (
    <div
      onClick={shouldBeDisabled ? undefined : onClick}
      className={cn(
        "relative bg-neutral-800/90 group rounded-2xl p-6 h-full flex flex-col",
        shouldBeDisabled ? "cursor-not-allowed" : "cursor-pointer",
        isLocked && "opacity-70",
        !shouldBeDisabled &&
          "transition-all duration-200 ease-out hover:-translate-y-1 hover:bg-neutral-800"
      )}
    >
      {/* Status indicator - top right */}
      {quest.status === ChallengeCampaignEventState.SUCCESSFUL && (
        <CheckCircle2 className="absolute top-4 right-4 w-6 h-6 text-green-500" />
      )}
      {quest.status === ChallengeCampaignEventState.PENDING && (
        <Clock className="absolute top-4 right-4 w-6 h-6 text-yellow-500" />
      )}

      {/* Icon at top */}
      <div className="flex items-center mb-6">
        {quest.media?.lightModeImageUrl ? (
          <div className="relative w-16 h-16">
            <img
              src={quest.media.lightModeImageUrl}
              alt={quest.title}
              className="w-full h-full object-contain"
            />
          </div>
        ) : (
          <QuestLogo size={64} className="text-white" />
        )}
      </div>

      {/* Title */}
      <h3 className="text-white font-semibold text-base md:text-lg mb-6 flex-1">
        {quest.title}
      </h3>

      {/* Footer with Points and Arrow */}
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-1.5">
          <span className="text-white font-bold text-lg">
            {formattedPoints}
          </span>
          <QuestLogo size={16} />
        </div>
        <ArrowRight className="w-5 h-5 text-white group-hover:translate-x-1 transition-transform" />
      </div>

      {/* Lock Overlay */}
      {isLocked && <LockOverlay unlockMessage={unlockMessage} />}
    </div>
  );
};
